'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["centered", "children", "className", "contextual", "dialogClassName", "_dialogWidth", "onEsc", "role", "size", "use", "_use", "_closeStartRelay", "_openCompleteRelay"];
import { first, last } from '../utils/underscore';
import { Children, Component, createRef } from 'react';
import classNames from 'classnames';
import devLogger from 'react-utils/devLogger';
import memoizeOne from 'react-utils/memoizeOne';
import { FOCUSABLE as FOCUSABLE_SELECTOR } from '../constants/Selectors';
import UIDialog from './UIDialog';
import UIDialogCloseButton from './UIDialogCloseButton';
import UIOverlay from '../overlay/UIOverlay';
import { matches } from '../utils/Dom';
import { ModalContextProvider } from '../context/ModalContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getContext = (use, headerCallback) => ({
  use,
  headerCallback
});
let hasPingedNewRelic = false;

// Report "special" modals that lack a proper header (#6712)
const reportDeprecatedModalMarkup = el => {
  if (!el) return;
  if (el.querySelector('.private-modal__header + .private-modal__body')) return;
  ['success', 'danger', 'info'].forEach(variant => {
    for (let headingLevel = 1; headingLevel <= 6; headingLevel++) {
      if (el.querySelector(`.private-modal--${variant} .private-modal__body h${headingLevel}`)) {
        if (process.env.NODE_ENV !== 'production') {
          devLogger.warn({
            message: `Modals with \`use="${variant}"\` should wrap their heading in a \`UIDialogHeader\`.`,
            key: 'legacy-modal-markup'
          });
        }
        if (window.newrelic && !hasPingedNewRelic) {
          window.newrelic.addPageAction('legacy-modal', {
            variant,
            headingLevel
          });
          hasPingedNewRelic = true;
        }
      }
    }
  });
};
class UIModalDialog extends Component {
  constructor(props) {
    super(props);
    this.overlayRef = /*#__PURE__*/createRef(null);
    this.handleOpenComplete = () => {
      this.setState({
        openComplete: true
      });
      this.startMutationObserver();
      this.captureFocus();
    };
    this.handleCloseStart = () => {
      if (!this._isMounted) return;
      this.stopMutationObserver();
    };
    this.startMutationObserver = () => {
      if (this._observer) return;
      const dialog = this.overlayRef.current;
      if (!dialog) return;

      // The focus should always be inside of the modal. We may need to recapture
      // it after an update because the focused element may have been unmounted.
      this._observer = new MutationObserver(this.captureFocus);
      this._observer.observe(dialog, {
        attributes: true,
        childList: true,
        subtree: true
      });

      // Disable the mutation observer during blur, as the value of
      // document.activeElement in that phase is misleading.
      addEventListener('blur', this.stopMutationObserver, {
        capture: true
      });
      addEventListener('focus', this.startMutationObserver, {
        capture: true
      });
    };
    this.stopMutationObserver = () => {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      removeEventListener('blur', this.stopMutationObserver, {
        capture: true
      });
      removeEventListener('focus', this.startMutationObserver, {
        capture: true
      });
    };
    this.captureFocus = () => {
      // If the focus is currently within the page or another dialog (or null/<body>),
      // focus on this dialog so that tabbing works. We *don't* want to capture
      // focus if the element is in a non-page/dialog element, e.g. a popover
      const dialog = this.overlayRef.current;
      if (!dialog) return;
      const activeElement = document.activeElement;
      const dialogContainsActiveElement = dialog.contains(activeElement);
      let shouldCapture = false;
      if (!activeElement || activeElement === document.body) {
        shouldCapture = true;
      } else if (!dialogContainsActiveElement) {
        // A few weird apps have multiple .page elements, also check for other dialogs...
        const containers = document.querySelectorAll('body > .page, div[role=dialog]');
        for (let i = 0; i < containers.length; i++) {
          if (containers[i].contains(activeElement)) {
            shouldCapture = true;
            break;
          }
        }
      }
      // Also, we also want to move focus to the dialog if the focus has somehow
      // wound up on a non-focusable element *within* the dialog.
      if (dialogContainsActiveElement && !matches(activeElement, FOCUSABLE_SELECTOR)) {
        shouldCapture = true;
      }
      if (shouldCapture) {
        dialog.focus();
      }
    };
    this.handleKeyDown = evt => {
      const {
        onEsc
      } = this.props;
      const dialog = this.overlayRef.current;
      if (!dialog) return;
      if (evt.key === 'Tab') {
        // Based on https://www.w3.org/TR/wai-aria-practices/#trap_focus_div
        const activeElement = document.activeElement;
        const allFocusableElements = dialog.querySelectorAll(FOCUSABLE_SELECTOR);
        const firstFocusableElement = first(allFocusableElements);
        const lastFocusableElement = last(allFocusableElements);
        if (evt.shiftKey) {
          // Shift+Tab: If focus is on first element, cycle to last element
          if (activeElement === firstFocusableElement || activeElement === dialog) {
            evt.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          // Unmodified Tab: If focus is on last element, cycle to first element
          if (activeElement === lastFocusableElement || activeElement === dialog) {
            evt.preventDefault();
            firstFocusableElement.focus();
          }
        }
      } else if (evt.key === 'Escape') {
        if (typeof onEsc === 'function') {
          onEsc(evt);
        } else {
          const closeButton = dialog.querySelector('[data-action=close]');
          if (closeButton) closeButton.click();
        }
      }
    };
    this.headerCallback = headerOverflow => {
      if (headerOverflow !== this.state.headerOverflow) {
        this.setState({
          headerOverflow
        });
      }
    };
    this.state = {
      headerOverflow: 0,
      openComplete: !props._openCompleteRelay
    };
    this._getContext = memoizeOne(getContext);
    if (props._openCompleteRelay) props._openCompleteRelay.subscribe(this.handleOpenComplete);
    if (props._closeStartRelay) props._closeStartRelay.subscribe(this.handleCloseStart);
  }
  componentDidMount() {
    this._isMounted = true;
    reportDeprecatedModalMarkup(this.overlayRef.current);
  }
  componentDidUpdate() {
    reportDeprecatedModalMarkup(this.overlayRef.current);
  }
  componentWillUnmount() {
    this._isMounted = false;
    this.stopMutationObserver();
  }
  render() {
    const _this$props = this.props,
      {
        centered,
        children,
        className,
        contextual,
        dialogClassName,
        _dialogWidth,
        role,
        size,
        use,
        _use
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      openComplete
    } = this.state;
    const computedUse = _use || use;

    // If one of the children is <UIDialogCloseButton detached>, render it in
    // the overlay instead of the dialog.
    let closeButton = null;
    const dialogChildren = Children.map(children, child => {
      if (child === null) {
        return null;
      } else if (child.type === UIDialogCloseButton && child.props.detached) {
        closeButton = child;
        return null;
      }
      return child;
    });
    const centerContent = ['fullscreen', 'sidebar', 'lightbox'].indexOf(computedUse) === -1;
    return /*#__PURE__*/_jsx(ModalContextProvider, {
      value: this._getContext(use, this.headerCallback),
      children: /*#__PURE__*/_jsxs(UIOverlay, Object.assign({}, rest, {
        centerContent: centerContent,
        className: classNames(className, 'private-modal-dialog--overlay'),
        contextual: contextual,
        "data-component-name": "UIModalDialog",
        "data-open-complete": openComplete,
        onKeyDown: this.handleKeyDown,
        overlayRef: this.overlayRef,
        tabIndex: -1,
        children: [/*#__PURE__*/_jsx(UIDialog, {
          _dialogWidth: _dialogWidth,
          centered: centered,
          className: dialogClassName,
          headerOverflow: this.state.headerOverflow,
          role: role !== undefined ? role : 'dialog',
          size: size,
          use: computedUse,
          "aria-modal": true,
          children: dialogChildren
        }), closeButton]
      }))
    });
  }
}
UIModalDialog.displayName = 'UIModalDialog';
UIModalDialog.defaultProps = {
  centered: false,
  contextual: false,
  use: 'default',
  size: 'auto',
  width: 450
};
export default UIModalDialog;